import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  // ...existing code...
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import emailTestService from "../../services/emailTestService";

/**
 * Componente para enviar correos de prueba
 * Útil para validar que la integración con Resend funciona correctamente
 */

const EmailTestDialog = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSendTestEmail = async () => {
    if (!email) {
      setError("El email es requerido");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await emailTestService.sendTestEmail(
        email,
        nombre || "Usuario Test",
        username || "test.user",
        password,
      );

      if (response.success || response.test === "SUCCESS") {
        setResult({
          success: true,
          message: response.message || "✓ Email enviado correctamente",
          details: response,
        });
        setError("");
      } else {
        setResult({
          success: false,
          message: response.message || "Error al enviar email",
          details: response,
        });
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.message || "Error desconocido";
      setError(errorMsg);
      setResult({
        success: false,
        message: "Error en la solicitud: " + errorMsg,
        details: err.response?.data || {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    if (!email) {
      setError("El email es requerido");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await emailTestService.sendWelcomeEmail(
        email,
        nombre || "Colaborador",
        username || "usuario",
        password,
      );

      if (response.success || response.test === "SUCCESS") {
        setResult({
          success: true,
          message: "✓ Email de bienvenida enviado correctamente",
          details: response,
        });
      } else {
        setResult({
          success: false,
          message: response.message || "Error al enviar email de bienvenida",
          details: response,
        });
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.message || "Error desconocido";
      setError(errorMsg);
      setResult({
        success: false,
        message: "Error: " + errorMsg,
        details: err.response?.data || {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmail("");
    setNombre("");
    setUsername("");
    setPassword("");
    setResult(null);
    setError("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>🧪 Prueba de Envío de Correos</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth
            type="email"
            label="Email de Destino"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ej: usuario@example.com"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="ej: Juan Pérez"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="ej: juan.perez"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          {result && (
            <Alert
              severity={result.success ? "success" : "error"}
              sx={{ mt: 2 }}
            >
              <strong>{result.message}</strong>
              {result.details?.resend_response?.id && (
                <div style={{ marginTop: "8px", fontSize: "12px" }}>
                  ID Resend: {result.details.resend_response.id}
                </div>
              )}
              {result.details?.http_code && (
                <div style={{ marginTop: "4px", fontSize: "12px" }}>
                  HTTP Code: {result.details.http_code}
                </div>
              )}
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              backgroundColor: "#f5f7fa",
              borderRadius: 1,
              fontSize: "12px",
              color: "#666",
            }}
          >
            <strong>ℹ️ Instrucciones:</strong>
            <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
              <li>
                Haz clic en "Prueba Rápida" para enviar un email de prueba
              </li>
              <li>Revisa tu bandeja de entrada y carpeta de spam</li>
              <li>
                Si recibes el email, Resend está funcionando correctamente
              </li>
              <li>
                Si no recibes nada, verifica los logs del servidor en error_log
              </li>
              <li>"Email de Bienvenida" simula el flujo real de creación</li>
            </ul>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button onClick={handleReset} disabled={loading}>
          Limpiar
        </Button>
        <Button
          onClick={handleSendWelcomeEmail}
          variant="contained"
          color="secondary"
          disabled={loading || !email}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          Email de Bienvenida
        </Button>
        <Button
          onClick={handleSendTestEmail}
          variant="contained"
          color="primary"
          disabled={loading || !email}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          Prueba Rápida
        </Button>
        <Button onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTestDialog;
